import { atom, selector, selectorFamily } from "recoil";
import {
  FORM_DEFAULTS,
  FORM_FIELD_DEFAULTS,
  FORM_FIELD_TOUCHED_DEFAULTS,
  FORM_TOUCHED_DEFAULTS,
} from "../constants/forms";
import { PaginatedResponse } from "../types/app";
import {
  FieldType,
  Form,
  FormCore,
  FormField,
  FormFieldRequest,
  FormFieldRequestTouched,
  FormRequest,
  FormRequestTouched,
} from "../types/forms";
import { getFieldTypeName } from "../utils/forms";

export const formsState = atom<PaginatedResponse<FormCore> | undefined>({
  key: "app.forms",
  default: undefined,
});

export const formsIdsState = selector<string[] | undefined>({
  key: "app.forms.ids",
  get: ({ get }) => {
    const forms = get(formsState);
    if (!forms) return;
    return forms.items.map((form) => form._id);
  },
});

export const formState = selectorFamily<FormCore | undefined, string>({
  key: "app.form.core",
  get:
    (id) =>
    ({ get }) => {
      const forms = get(formsState);
      return forms?.items.find((form) => form._id === id);
    },
});

export const formDetailState = atom<Form | undefined>({
  key: "app.form",
  default: undefined,
});

export const formDetailFieldsState = selector<FormField[]>({
  key: "app.form.fields",
  get: ({ get }) => {
    const formDetail = get(formDetailState);
    return formDetail ? formDetail.fields : [];
  },
});

export const searchFormsState = atom<string | undefined>({
  key: "app.forms.search",
  default: undefined,
});

export const formsPageState = atom({
  key: "app.forms.page",
  default: 1,
});

export const updateFormsState = atom<string | undefined>({
  key: "app.forms.update",
  default: undefined,
});

export const loadingFormsState = atom({
  key: "app.forms.loading",
  default: false,
});

export const selectedFormIdState = atom<string | undefined>({
  key: "app.forms.selected",
  default: undefined,
});

export const updateFormFieldsState = atom<"update" | undefined>({
  key: "app.forms.fields.update",
  default: undefined,
});

// FORM
export const editFormState = atom<FormRequest>({
  key: "app.forms.edit",
  default: selector({
    key: "app.forms.editing.initializer",
    get: ({ get }) => {
      const selectedFormId = get(selectedFormIdState);
      if (!selectedFormId) {
        return FORM_DEFAULTS;
      }
      const form = get(formState(selectedFormId));
      return form ? { ...form, tenants: form.tenants.map((t) => t._id) } : FORM_DEFAULTS;
    },
  }),
});

export const editFormTouchedState = atom<FormRequestTouched>({
  key: "app.forms.edit.touched",
  default: FORM_TOUCHED_DEFAULTS,
});

export const editFormValidState = selector<{
  [key in keyof FormRequest]: boolean;
}>({
  key: "app.forms.edit.valid",
  get: ({ get }) => {
    const fields = get(editFormState);

    // SET ALL FIELDS TO BE VALID
    const validFields = Object.fromEntries(Object.keys(fields).map((key) => [key, true])) as {
      [key in keyof FormRequest]: boolean;
    };

    // VALIDATE REQUIRED FIELDS
    if (!fields.name) {
      validFields.name = false;
    }

    return validFields;
  },
});

export const editFormErrorState = selector({
  key: "app.forms.edit.error",
  get: ({ get }) => {
    const validFields = get(editFormValidState);
    return Object.values(validFields).some((valid) => !valid);
  },
});

// FORM FIELD
export const selectedFormFieldState = atom<
  { id: string; index: number; uncreated: boolean; fieldType?: FieldType } | undefined
>({
  key: "app.forms.fields.selected",
  default: undefined,
});

export const editFormFieldState = atom<FormFieldRequest>({
  key: "app.forms.field.edit",
  default: selector({
    key: "app.forms.field.edit.initializer",
    get: ({ get }) => {
      const selectedFormField = get(selectedFormFieldState);
      if (!selectedFormField) return FORM_FIELD_DEFAULTS;
      const formDetailFields = get(formDetailFieldsState);
      const formField = formDetailFields.find((field) => field._id === selectedFormField.id);
      return formField
        ? formField
        : {
            ...FORM_FIELD_DEFAULTS,
            _id: selectedFormField.id,
            fieldType: selectedFormField.fieldType ?? FORM_FIELD_DEFAULTS.fieldType,
            name: getFieldTypeName(selectedFormField.fieldType ?? FORM_FIELD_DEFAULTS.fieldType),
            order: selectedFormField.index,
          };
    },
  }),
});

export const editFormFieldTouchedState = atom<FormFieldRequestTouched>({
  key: "app.forms.field.edit.touched",
  default: FORM_FIELD_TOUCHED_DEFAULTS,
});

export const editFormFieldValidState = selector<{
  [key in keyof FormFieldRequest]: boolean;
}>({
  key: "app.forms.field.edit.valid",
  get: ({ get }) => {
    const fields = get(editFormFieldState);

    // SET ALL FIELDS TO BE VALID
    const validFields = Object.fromEntries(Object.keys(fields).map((key) => [key, true])) as {
      [key in keyof FormFieldRequest]: boolean;
    };

    // VALIDATE REQUIRED FIELDS
    if (!fields.fieldType) {
      validFields.fieldType = false;
    }
    if (!fields.name) {
      validFields.name = false;
    }
    if (!fields.order.toString() || !(fields.order >= 0)) {
      validFields.order = false;
    }

    return validFields;
  },
});

export const editFormFieldErrorState = selector<boolean>({
  key: "app.forms.field.edit.error",
  get: ({ get }) => {
    const validFields = get(editFormFieldValidState);
    return Object.values(validFields).some((valid) => !valid);
  },
});
